import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useTaxonomyContext } from '../context/TaxonomyContext';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { TaxonomyGetResponse } from '@/services/TaxonomiesService';

const taxonomySchema = z.object({
  categoryName: z.string().min(1, 'Category is required'),
  type: z.string().min(1, 'Type is required'),
  groupName: z.string().min(1, 'Group Name is required'),
  property: z.string().min(1, 'Property is required'),
  description: z.string().min(1, 'Description is required'),
  examples: z.string().min(1, 'Examples are required'),
  filterValues: z.string().optional(),
  order: z.number().min(0, 'Order must be a positive number'),
});

type TaxonomyFormData = z.infer<typeof taxonomySchema>;

interface EditTaxonomyModalProps {
  editingItem: TaxonomyGetResponse;
  isOpen: boolean;
  onClose: () => void;
}

export function EditTaxonomyForm({
  editingItem,
  isOpen,
  onClose,
}: EditTaxonomyModalProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TaxonomyFormData>({
    resolver: zodResolver(taxonomySchema),
    defaultValues: {
      categoryName: editingItem?.categoryName || '',
      type: editingItem?.type || '',
      groupName: editingItem?.groupName || '',
      property: editingItem?.property || '',
      description: editingItem?.description || '',
      examples: editingItem?.examples || '',
      filterValues: editingItem.filterValues?.join(',') || '',
      order: editingItem?.order || 0,
    },
  });
  const { setTaxonomiesUpdated, properties } = useTaxonomyContext();

  function areArraysEqual(a: string[] = [], b: string[] = []) {
    if (a.length !== b.length) return false;
    return a.every((val, i) => val === b[i]);
  }

  function onSubmit(data: TaxonomyFormData) {
    const updatedData = {
      ...data,
      id: editingItem.id,
      filterValues: data.filterValues
        ?.split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    };

    const existsInProperties = properties.some((item) => {
      if (editingItem.id) {
        return item.id === editingItem.id;
      } else {
        return (
          item.categoryName === updatedData.categoryName &&
          item.groupName === updatedData.groupName &&
          item.property === updatedData.property
        );
      }
    });

    setTaxonomiesUpdated((prev) => {
      const newData = [...prev.data];
      let indexToUpdate = -1;

      if (editingItem.id) {
        indexToUpdate = newData.findIndex((item) => item.id === editingItem.id);
      } else {
        indexToUpdate = newData.findIndex(
          (item) =>
            item.categoryName === updatedData.categoryName &&
            item.groupName === updatedData.groupName &&
            item.property === updatedData.property &&
            item.filterValues === updatedData.filterValues,
        );
      }

      if (existsInProperties) {
        const originalProperty = properties.find((item) => {
          if (editingItem.id) {
            return item.id === editingItem.id;
          } else {
            return (
              item.categoryName === updatedData.categoryName &&
              item.groupName === updatedData.groupName &&
              item.property === updatedData.property &&
              item.filterValues === updatedData.filterValues
            );
          }
        });

        const isIdentical =
          originalProperty &&
          originalProperty.categoryName === updatedData.categoryName &&
          originalProperty.type === updatedData.type &&
          originalProperty.groupName === updatedData.groupName &&
          originalProperty.property === updatedData.property &&
          originalProperty.description === updatedData.description &&
          originalProperty.examples === updatedData.examples &&
          areArraysEqual(
            originalProperty.filterValues,
            updatedData.filterValues,
          ) &&
          originalProperty.order === updatedData.order;

        if (isIdentical && indexToUpdate !== -1) {
          newData.splice(indexToUpdate, 1);
          toast.success('Taxonomy reverted to original state.');
          onClose();
          return {
            ...prev,
            data: newData,
          };
        }
      }

      if (indexToUpdate !== -1) {
        newData[indexToUpdate] = updatedData;
      } else {
        newData.push(updatedData);
      }

      toast.success('Taxonomy updated, save changes at the end.');
      onClose();
      return {
        ...prev,
        data: newData,
      };
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="mx-auto max-w-lg rounded-xl bg-white p-6 shadow-lg">
        <DialogHeader>
          <DialogTitle>Edit Taxonomy</DialogTitle>

          <DialogDescription>
            Make changes to the taxonomy property.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <FormGroup
                error={{ message: errors.categoryName?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="category">Category</RequiredLabel>
                <Controller
                  name="categoryName"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>

              <FormGroup error={{ message: errors.type?.message, font: 'sm' }}>
                <RequiredLabel htmlFor="type">Type</RequiredLabel>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormGroup
                error={{ message: errors.groupName?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="groupName">Group Name</RequiredLabel>
                <Controller
                  name="groupName"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>

              <FormGroup
                error={{ message: errors.property?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="property">Property</RequiredLabel>
                <Controller
                  name="property"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>
            </div>

            <FormGroup
              error={{ message: errors.description?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="description">Description</RequiredLabel>
              <Controller
                name="description"
                control={control}
                render={({ field }) => <Textarea {...field} rows={3} />}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.examples?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="examples">Examples</RequiredLabel>
              <Controller
                name="examples"
                control={control}
                render={({ field }) => <Textarea {...field} rows={3} />}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.filterValues?.message, font: 'sm' }}
            >
              <Label htmlFor="filterValues">Filter Values</Label>
              <Controller
                name="filterValues"
                control={control}
                render={({ field }) => <Textarea {...field} rows={3} />}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.order?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="order">Order</RequiredLabel>
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                )}
              />
            </FormGroup>
          </div>

          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
